import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Package, Shirt, FileText, Plus, Minus, ArrowRight, CheckCircle, Truck, Calendar } from 'lucide-react';
import CustomerBottomNav from '@/components/CustomerBottomNav';
import CustomerSidebar from '@/components/CustomerSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLOTHES_QUICK = ['Shirts', 'Jeans', 'T-Shirts', 'Bedsheets', 'Towels', 'Kurta', 'Trousers', 'Sarees'];

const CustomerNewOrder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState({ total_clothes: 1, clothes_type: '', special_instructions: '' });
  const [selectedTypes, setSelectedTypes] = useState([]);

  const token = localStorage.getItem('customerToken');

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      const next = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type];
      setOrderData(o => ({ ...o, clothes_type: next.join(', ') }));
      return next;
    });
  };

  const adjust = (delta) => {
    setOrderData(o => ({ ...o, total_clothes: Math.max(1, (o.total_clothes || 1) + delta) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderData.total_clothes || orderData.total_clothes < 1) {
      toast.error('Please enter at least 1 clothing item');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/customer/orders`, {
        total_clothes: parseInt(orderData.total_clothes),
        clothes_type: orderData.clothes_type || null,
        special_instructions: orderData.special_instructions || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order placed successfully!');
      navigate('/customer/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <CustomerSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/customer/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>New Order</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Desktop title */}
            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Place New Order</h1>
              <p className="text-sm text-slate-500 mt-0.5">Your laundry will be picked up on the next scheduled day</p>
            </div>

            {/* ── How it works strip ── */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-5 shadow-sm">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">How it works</p>
              <div className="flex items-center justify-between">
                {[
                  { icon: Package, label: 'Place Order', color: 'bg-blue-50 text-blue-600' },
                  { icon: Truck,   label: 'We Pickup',   color: 'bg-orange-50 text-orange-600' },
                  { icon: Shirt,   label: 'We Wash',     color: 'bg-violet-50 text-violet-600' },
                  { icon: CheckCircle, label: 'Delivered', color: 'bg-emerald-50 text-emerald-600' },
                ].map(({ icon: Icon, label, color }, i, arr) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center gap-1.5">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 text-center">{label}</span>
                    </div>
                    {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ── Clothes count ── */}
              <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>How many clothes?</h3>
                    <p className="text-xs text-slate-400">Count all items you want to send</p>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-6">
                  <button type="button" onClick={() => adjust(-1)}
                    className="w-14 h-14 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all hover:scale-105 active:scale-95">
                    <Minus className="w-6 h-6" />
                  </button>
                  <div className="text-center">
                    <span className="text-5xl font-black text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {orderData.total_clothes}
                    </span>
                    <p className="text-sm text-slate-400 mt-1">items</p>
                  </div>
                  <button type="button" onClick={() => adjust(1)}
                    className="w-14 h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md shadow-blue-200">
                    <Plus className="w-6 h-6" />
                  </button>
                </div>

                {/* Quick set */}
                <div className="flex justify-center gap-2 mt-5 flex-wrap">
                  {[5, 10, 15, 20].map(n => (
                    <button key={n} type="button"
                      onClick={() => setOrderData(o => ({ ...o, total_clothes: n }))}
                      className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${orderData.total_clothes === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                      {n} items
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Clothes type ── */}
              <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Shirt className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>What type of clothes?</h3>
                    <p className="text-xs text-slate-400">Optional — tap to select</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {CLOTHES_QUICK.map(type => (
                    <button key={type} type="button" onClick={() => toggleType(type)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${selectedTypes.includes(type) ? 'bg-orange-500 text-white border-orange-500 shadow-sm' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-orange-300'}`}>
                      {type}
                    </button>
                  ))}
                </div>

                <textarea
                  value={orderData.clothes_type}
                  onChange={e => { setOrderData(o => ({ ...o, clothes_type: e.target.value })); setSelectedTypes([]); }}
                  placeholder="Or type custom items (e.g., silk sarees, woollen sweaters)..."
                  rows={2}
                  data-testid="order-clothes-type"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 hover:border-orange-300 focus:border-orange-400 rounded-xl text-sm text-slate-700 placeholder-slate-400 outline-none transition-colors resize-none"
                />
              </div>

              {/* ── Special instructions ── */}
              <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Special Instructions</h3>
                    <p className="text-xs text-slate-400">Optional — any care notes for us</p>
                  </div>
                </div>
                <textarea
                  value={orderData.special_instructions}
                  onChange={e => setOrderData(o => ({ ...o, special_instructions: e.target.value }))}
                  placeholder="e.g., Handle silk delicately, separate whites from colours..."
                  rows={3}
                  data-testid="order-instructions"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 hover:border-violet-300 focus:border-violet-400 rounded-xl text-sm text-slate-700 placeholder-slate-400 outline-none transition-colors resize-none"
                />
              </div>

              {/* ── Order summary + submit ── */}
              <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Order Summary</p>
                <div className="space-y-2.5 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total Items</span>
                    <span className="font-bold text-slate-800">{orderData.total_clothes} items</span>
                  </div>
                  {orderData.clothes_type && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Type</span>
                      <span className="font-medium text-slate-700 text-right max-w-[60%] truncate">{orderData.clothes_type}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-xs text-slate-500">Pickup on your next scheduled PG pickup day</span>
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="place-order-submit"
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-blue-200 hover:-translate-y-0.5 text-base">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <><Package className="w-5 h-5" /> Place Order</>
                  )}
                </button>
              </div>
            </form>

          </div>
        </main>
      </div>

      <CustomerBottomNav />
    </div>
  );
};

export default CustomerNewOrder;
